import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../context/AppContext';
import AdminNavbar from '../components/AdminNavbar';
import toast from 'react-hot-toast';

const LIMIT = 15;

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric',
}) : '—';

const Avatar = ({ user, size = 36 }) => {
  const initials = user.name
    ? user.name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()
    : user.phone?.slice(-2) || 'U';
  const bg = user.role === 'admin'
    ? 'linear-gradient(135deg,#d97706,#f59e0b)'
    : 'linear-gradient(135deg,#2D8B4E,#4CAF72)';
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.33, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
      {initials}
    </div>
  );
};

const RoleBadge = ({ role }) => (
  <span style={{
    fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 100,
    background: role === 'admin' ? 'rgba(217,119,6,0.12)' : 'rgba(45,139,78,0.1)',
    color:      role === 'admin' ? '#d97706'              : '#2D8B4E',
    border:     `1px solid ${role === 'admin' ? 'rgba(217,119,6,0.25)' : 'rgba(45,139,78,0.22)'}`,
  }}>
    {role === 'admin' ? '⭐ Admin' : '👤 User'}
  </span>
);

export default function AdminUsers() {
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [total,       setTotal]       = useState(0);
  const [totalPages,  setTotalPages]  = useState(1);
  const [page,        setPage]        = useState(1);
  const [search,      setSearch]      = useState('');
  const [roleFilter,  setRoleFilter]  = useState('all');
  const [selected,    setSelected]    = useState(null);
  const [actionId,    setActionId]    = useState(null);
  const debounceRef = useRef(null);

  const fetchUsers = useCallback(async (pg = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: LIMIT });
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (search.trim())        params.append('search', search.trim());
      const res = await api.get(`/admin/users?${params}`);
      setUsers(res.data?.users || res.data || []);
      setTotal(res.data?.total || 0);
      setTotalPages(res.data?.pages || Math.ceil((res.data?.total || 0) / LIMIT) || 1);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [page, roleFilter, search]);

  useEffect(() => { fetchUsers(page); }, [page, roleFilter]); // eslint-disable-line
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); fetchUsers(1); }, 400);
  }, [search]); // eslint-disable-line

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user and all their data?')) return;
    setActionId(id);
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success('User deleted');
      setSelected(null);
      fetchUsers(page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    } finally { setActionId(null); }
  };

  const handleRoleToggle = async (user) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    if (!window.confirm(`Make ${user.name || user.phone} a${newRole === 'admin' ? 'n admin' : ' regular user'}?`)) return;
    setActionId(user._id);
    try {
      await api.put(`/admin/users/${user._id}/role`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      fetchUsers(page);
      if (selected?._id === user._id) setSelected(prev => ({ ...prev, role: newRole }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update role');
    } finally { setActionId(null); }
  };

  /* windowed pagination */
  const pageList = () => {
    const out = [];
    for (let p = 1; p <= totalPages; p++) {
      if (p === 1 || p === totalPages || (p >= page - 2 && p <= page + 2)) out.push(p);
    }
    const withGaps = [];
    out.forEach((p, i) => {
      if (i > 0 && p - out[i-1] > 1) withGaps.push('…');
      withGaps.push(p);
    });
    return withGaps;
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#f0f9f0,#e8f5e9)', fontFamily: "'Roboto',sans-serif" }}>
      <AdminNavbar />

      {/* hero */}
      <div className="page-gradient py-6">
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ color: '#fff', fontSize: 26, fontWeight: 900, margin: '0 0 3px' }}>👥 Users</h1>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, margin: 0 }}>{total} registered users · manage roles · remove accounts</p>
          </div>
          <button onClick={() => fetchUsers(page)}
            style={{ padding: '9px 20px', borderRadius: 100, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.25)' }}>
            ↻ Refresh
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 20px 60px', boxSizing: 'border-box' }}>
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden' }}>

          {/* filters */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #f5f5f5', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 220px' }}>
              <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13 }}>🔍</span>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, phone or email…"
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px 8px 32px', borderRadius: 10, border: '1.5px solid #e5e7eb', fontSize: 13, outline: 'none', background: '#f9fafb', color: '#111827' }}
                onFocus={e => { e.target.style.borderColor = '#2D8B4E'; e.target.style.background = '#fff'; }}
                onBlur={e  => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#f9fafb'; }} />
            </div>
            <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}
              style={{ padding: '8px 12px', borderRadius: 10, border: '1.5px solid #e5e7eb', fontSize: 13, fontWeight: 600, color: '#374151', background: '#fff', outline: 'none', cursor: 'pointer' }}>
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
            <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto', whiteSpace: 'nowrap' }}>{total} user{total !== 1 ? 's' : ''}</span>
          </div>

          {/* table */}
          {loading ? (
            <div style={{ padding: '60px 0', textAlign: 'center' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #e5e7eb', borderTopColor: '#2D8B4E', animation: 'spin 0.7s linear infinite', margin: '0 auto 10px' }} />
              <p style={{ fontSize: 13, color: '#9ca3af' }}>Loading users…</p>
            </div>
          ) : users.length === 0 ? (
            <div style={{ padding: '60px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>👥</div>
              <p style={{ fontWeight: 700, color: '#374151' }}>No users found</p>
              {search && (
                <button onClick={() => setSearch('')}
                  style={{ marginTop: 10, fontSize: 13, color: '#2D8B4E', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <>
              {/* col headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.4fr 0.8fr 0.8fr 1fr', gap: 0, padding: '10px 20px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                {['User', 'Phone', 'Email', 'Role', 'Joined', 'Actions'].map(h => (
                  <span key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.6px' }}>{h}</span>
                ))}
              </div>

              {users.map((u, idx) => (
                <div key={u._id}
                  style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.4fr 0.8fr 0.8fr 1fr', gap: 0, padding: '12px 20px', borderBottom: idx < users.length-1 ? '1px solid #f9fafb' : 'none', alignItems: 'center', cursor: 'pointer', transition: 'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => setSelected(u)}>

                  {/* user */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar user={u} size={34} />
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || <span style={{ color: '#d1d5db', fontWeight: 400 }}>No name</span>}</p>
                    </div>
                  </div>

                  {/* phone */}
                  <span style={{ fontSize: 12, color: '#374151', fontFamily: 'monospace' }}>{u.phone}</span>

                  {/* email */}
                  <span style={{ fontSize: 12, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email || '—'}</span>

                  {/* role */}
                  <div><RoleBadge role={u.role} /></div>

                  {/* joined */}
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(u.createdAt)}</span>

                  {/* actions */}
                  <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => handleRoleToggle(u)}
                      disabled={actionId === u._id}
                      title={u.role === 'admin' ? 'Demote to user' : 'Promote to admin'}
                      style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, border: `1px solid ${u.role === 'admin' ? 'rgba(217,119,6,0.3)' : 'rgba(45,139,78,0.3)'}`, background: '#fff', color: u.role === 'admin' ? '#d97706' : '#2D8B4E', cursor: 'pointer', opacity: actionId === u._id ? 0.6 : 1 }}>
                      {u.role === 'admin' ? '↓ Demote' : '↑ Promote'}
                    </button>
                    <button
                      onClick={() => handleDelete(u._id)}
                      disabled={actionId === u._id}
                      title="Delete user"
                      style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#ef4444', cursor: 'pointer', opacity: actionId === u._id ? 0.6 : 1 }}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}

              {/* pagination */}
              {totalPages > 1 && (
                <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>
                    Showing <strong style={{ color: '#374151' }}>{(page-1)*LIMIT+1}–{Math.min(page*LIMIT,total)}</strong> of <strong style={{ color: '#374151' }}>{total}</strong> users
                  </span>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
                      style={{ height:30, padding:'0 12px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'#fff', fontSize:12, fontWeight:700, color:page===1?'#d1d5db':'#374151', cursor:page===1?'not-allowed':'pointer' }}>
                      ← Prev
                    </button>
                    {pageList().map((p, i) =>
                      p === '…' ? (
                        <span key={`g${i}`} style={{ width:30, textAlign:'center', fontSize:13, color:'#9ca3af' }}>…</span>
                      ) : (
                        <button key={p} onClick={() => setPage(p)}
                          style={{ width:30, height:30, borderRadius:8, border:'1.5px solid', borderColor:p===page?'#2D8B4E':'#e5e7eb', background:p===page?'#2D8B4E':'#fff', color:p===page?'#fff':'#374151', fontSize:12, fontWeight:p===page?800:500, cursor:'pointer' }}>
                          {p}
                        </button>
                      )
                    )}
                    <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
                      style={{ height:30, padding:'0 12px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'#fff', fontSize:12, fontWeight:700, color:page===totalPages?'#d1d5db':'#374151', cursor:page===totalPages?'not-allowed':'pointer' }}>
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* user detail modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }} onClick={() => setSelected(null)} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 20, width: '100%', maxWidth: 480, boxShadow: '0 24px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>

            {/* header */}
            <div style={{ padding: '20px 22px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 14 }}>
              <Avatar user={selected} size={52} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <h2 style={{ fontSize: 16, fontWeight: 900, color: '#111827', margin: 0 }}>{selected.name || 'No name set'}</h2>
                  <RoleBadge role={selected.role} />
                </div>
                <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>Joined {fmtDate(selected.createdAt)}</p>
              </div>
              <button onClick={() => setSelected(null)}
                style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: '#f3f4f6', color: '#6b7280', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
            </div>

            {/* details */}
            <div style={{ padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Phone',   value: selected.phone   },
                { label: 'Email',   value: selected.email || '—' },
                { label: 'Street',  value: selected.address?.street || '—'  },
                { label: 'City',    value: selected.address?.city || '—'    },
                { label: 'Pincode', value: selected.address?.pincode || '—' },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f9fafb' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>{row.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{row.value}</span>
                </div>
              ))}
            </div>

            {/* actions */}
            <div style={{ padding: '14px 22px 20px', display: 'flex', gap: 10 }}>
              <button onClick={() => handleRoleToggle(selected)} disabled={actionId === selected._id}
                style={{ flex: 1, padding: '10px', borderRadius: 10, border: `1.5px solid ${selected.role === 'admin' ? 'rgba(217,119,6,0.3)' : 'rgba(45,139,78,0.3)'}`, background: selected.role === 'admin' ? 'rgba(217,119,6,0.05)' : 'rgba(45,139,78,0.05)', color: selected.role === 'admin' ? '#d97706' : '#2D8B4E', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: actionId === selected._id ? 0.6 : 1 }}>
                {selected.role === 'admin' ? '↓ Demote to User' : '↑ Promote to Admin'}
              </button>
              <button onClick={() => handleDelete(selected._id)} disabled={actionId === selected._id}
                style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #fecaca', background: '#fff', color: '#ef4444', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: actionId === selected._id ? 0.6 : 1 }}>
                🗑 Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}